import { useEffect, useMemo, useState } from "react";
import type { ICity, ICountry, IState } from "country-state-city";

let geoModule: typeof import("country-state-city") | null = null;

async function loadGeo() {
  if (!geoModule) {
    geoModule = await import("country-state-city");
  }
  return geoModule;
}

export interface GeoSelection {
  countryCode: string;
  countryName: string;
  stateCode: string;
  stateName: string;
  cityName: string;
}

interface GeoLocationFieldsProps {
  countryCode: string;
  stateCode: string;
  cityName: string;
  onChange: (value: GeoSelection) => void;
}

export function GeoLocationFields({
  countryCode,
  stateCode,
  cityName,
  onChange,
}: GeoLocationFieldsProps) {
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [states, setStates] = useState<IState[]>([]);
  const [cities, setCities] = useState<ICity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void loadGeo().then(({ Country }) => {
      if (cancelled) return;
      setCountries(Country.getAllCountries());
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!countryCode) {
      setStates([]);
      setCities([]);
      return;
    }
    let cancelled = false;
    void loadGeo().then(({ State }) => {
      if (cancelled) return;
      setStates(State.getStatesOfCountry(countryCode));
    });
    return () => {
      cancelled = true;
    };
  }, [countryCode]);

  useEffect(() => {
    if (!countryCode || !stateCode) {
      setCities([]);
      return;
    }
    let cancelled = false;
    void loadGeo().then(({ City }) => {
      if (cancelled) return;
      setCities(City.getCitiesOfState(countryCode, stateCode));
    });
    return () => {
      cancelled = true;
    };
  }, [countryCode, stateCode]);

  const country = useMemo(
    () => countries.find((c) => c.isoCode === countryCode),
    [countries, countryCode],
  );
  const state = useMemo(
    () => states.find((s) => s.isoCode === stateCode),
    [states, stateCode],
  );

  function emit(next: Partial<GeoSelection>) {
    onChange({
      countryCode: next.countryCode ?? countryCode,
      countryName: next.countryName ?? country?.name ?? "",
      stateCode: next.stateCode ?? stateCode,
      stateName: next.stateName ?? state?.name ?? "",
      cityName: next.cityName ?? cityName,
    });
  }

  if (loading) {
    return <p className="small text-secondary mb-0">Chargement des pays…</p>;
  }

  return (
    <div className="row g-3">
      <div className="col-md-4">
        <label className="form-label">Pays *</label>
        <select
          className="form-select"
          value={countryCode}
          required
          onChange={(e) => {
            const c = countries.find((x) => x.isoCode === e.target.value);
            emit({
              countryCode: e.target.value,
              countryName: c?.name ?? "",
              stateCode: "",
              stateName: "",
              cityName: "",
            });
          }}
        >
          <option value="">— Choisir —</option>
          {countries.map((c) => (
            <option key={c.isoCode} value={c.isoCode}>
              {c.flag} {c.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-4">
        <label className="form-label">Province / État *</label>
        <select
          className="form-select"
          value={stateCode}
          required
          disabled={!countryCode || states.length === 0}
          onChange={(e) => {
            const s = states.find((x) => x.isoCode === e.target.value);
            emit({
              stateCode: e.target.value,
              stateName: s?.name ?? "",
              cityName: "",
            });
          }}
        >
          <option value="">— Choisir —</option>
          {states.map((s) => (
            <option key={s.isoCode} value={s.isoCode}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-4">
        <label className="form-label">Ville *</label>
        {cities.length > 0 ? (
          <select
            className="form-select"
            value={cityName}
            required
            disabled={!stateCode}
            onChange={(e) => emit({ cityName: e.target.value })}
          >
            <option value="">— Choisir —</option>
            {cities.map((c) => (
              <option key={`${c.stateCode}-${c.name}`} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        ) : (
          <input
            className="form-control"
            value={cityName}
            required
            disabled={!stateCode && !countryCode}
            placeholder="Nom de la ville"
            onChange={(e) => emit({ cityName: e.target.value })}
          />
        )}
      </div>
    </div>
  );
}
